import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiBook, FiStar, FiDollarSign, FiShoppingCart, FiLock } from 'react-icons/fi';
import LoadingSpinner from './LoadingSpinner';
import Button from './Button';
import { useTheme } from '../../contexts/ThemeContext';
import { useCourseOwnership } from '../../contexts/CourseOwnershipContext';
import { 
  ContentCard,
  CourseThumbnail,
  ContentCardHeader,
  ContentCardBody,
} from '../../pages/dashboards/Dashboard.styles';

const CourseCard = ({ 
  course, 
  showProgress = false, 
  progressPercentage = 0,
  showRating = true,
  showInstructor = true,
  showGrade = true,
  showPrice = false,
  showPurchaseButton = false,
  onPurchaseClick,
  onClick,
  className = '',
  style = {}
}) => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { ownsCourse } = useCourseOwnership();
  
  // Handle different thumbnail URL structures from different API responses
  const thumbnailUrl = course.thumbnail_url || 
                       course.course?.thumbnail_url || 
                       course.thumbnail;
  
  const [imageLoading, setImageLoading] = useState(!!thumbnailUrl);
  const [imageError, setImageError] = useState(false);

  const handleCardClick = () => {
    if (onClick) {
      onClick(course);
    } else {
      navigate(`/learner/courses/${course.id || course.course_id || course.course_content_id}`);
    }
  };

  const renderStars = (rating) => {
    return (
      <div style={{ display: 'flex', gap: '2px' }}>
        {[1, 2, 3, 4, 5].map((star) => (
          <FiStar
            key={star}
            size={12}
            color={star <= rating ? '#F59E0B' : '#E5E7EB'}
            fill={star <= rating ? '#F59E0B' : 'none'}
          />
        ))}
      </div>
    );
  };

  const getStatusBadge = () => {
    if (course.is_completed || course.completed) {
      return {
        text: 'Completed',
        color: '#10B981'
      };
    } else if (course.started || course.is_started) {
      return {
        text: 'In Progress',
        color: '#2c5f34'
      };
    } else if (course.recommended) {
      return {
        text: 'Recommended',
        color: '#F59E0B'
      };
    } else if (course.is_published !== undefined) {
      return {
        text: course.is_published ? 'Published' : 'Draft',
        color: course.is_published ? '#10B981' : '#F59E0B'
      };
    }
    return null;
  };

  const statusBadge = getStatusBadge();
  const courseTitle = course.title || course.course_title;
  const courseId = course.id || course.course_id || course.course_content_id;
  const instructor = course.teacher?.username || 
                    course.instructor?.username || 
                    course.course?.teacher?.username;
  const grade = course.grade?.name || 
               course.grade_name || 
               course.course?.grade?.name;
  const rating = course.average_rating || 
                course.rating || 
                course.course?.average_rating;
  const ratingCount = course.rating_count || 
                     course.course?.rating_count;
  const price = course.price !== undefined ? course.price : null;
  const isOwned = ownsCourse(courseId);
  
  // Debug: Log thumbnail URL to help identify issues
  if (process.env.NODE_ENV === 'development' && courseTitle) {
    console.log(`CourseCard[${courseTitle}] thumbnailUrl:`, thumbnailUrl);
  }

  return (
    <ContentCard 
      onClick={handleCardClick}
      style={{ cursor: 'pointer', ...style }}
      className={className}
    >
      {/* Course Thumbnail with Status Badge Overlay */}
      <CourseThumbnail>
        {thumbnailUrl && !imageError ? (
          <>
            {imageLoading && (
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                zIndex: 2,
              }}>
                <LoadingSpinner size="medium" />
              </div>
            )}
            <img 
              src={thumbnailUrl} 
              alt={courseTitle}
              style={{ display: imageLoading ? 'none' : 'block' }}
              onLoad={() => setImageLoading(false)}
              onError={() => {
                setImageLoading(false);
                setImageError(true);
              }}
            />
            <div className="no-thumbnail" style={{ display: 'none' }}>
              <FiBook />
              <span>Course Thumbnail</span>
            </div>
          </>
        ) : (
          <div className="no-thumbnail">
            <FiBook />
            <span>Course Thumbnail</span>
          </div>
        )}
        
        {/* Status Badge on Thumbnail (like Udemy) */}
        {statusBadge && (
          <div style={{
            position: 'absolute',
            top: '12px',
            left: '12px',
            padding: '6px 12px',
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: '600',
            background: statusBadge.color,
            color: 'white',
            zIndex: 3,
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
          }}>
            {statusBadge.text}
          </div>
        )}
        
        {/* Progress Bar Overlay on Thumbnail */}
        {showProgress && progressPercentage > 0 && !course.is_completed && !course.completed && (
          <div style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: 'rgba(0, 0, 0, 0.3)',
            zIndex: 3,
          }}>
            <div style={{
              width: `${progressPercentage}%`,
              height: '100%',
              background: progressPercentage >= 80 ? '#10B981' : 
                         progressPercentage >= 60 ? '#F59E0B' : '#EF4444',
              transition: 'width 0.3s ease',
            }} />
          </div>
        )}
      </CourseThumbnail>

      <ContentCardHeader>
        <div>
          <h3>{courseTitle}</h3>
        </div>
      </ContentCardHeader>
      
      <ContentCardBody>
        {(showInstructor || showGrade) && (
          <div className="meta">
            {showInstructor && (
              <span>👨‍🏫 {instructor || 'Unknown Teacher'}</span>
            )}
            {showGrade && (
              <span>📚 {grade || 'Unknown Grade'}</span>
            )}
          </div>
        )}
        
        {/* Ratings */}
        {showRating && rating > 0 && (
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '12px',
            marginTop: '8px',
            flexWrap: 'wrap'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span style={{ 
                fontWeight: '600', 
                color: '#F59E0B',
                fontSize: '14px'
              }}>
                {rating.toFixed(1)}
              </span>
              {renderStars(Math.round(rating))}
            </div>
            <span style={{ 
              fontSize: '12px', 
              color: theme.colors.textTertiary 
            }}>
              ({ratingCount || 0})
            </span>
          </div>
        )}
        
        {        /* Custom content slot */}
        {course.customContent && (
          <div style={{ marginTop: '8px' }}>
            {course.customContent}
          </div>
        )}
        
        {/* Price and Purchase Button */}
        {showPrice && price !== null && (
          <div style={{ 
            marginTop: '12px',
            paddingTop: '12px',
            borderTop: `1px solid ${theme.colors.border}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px'
          }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center',
              gap: '4px',
              fontSize: '18px',
              fontWeight: '700',
              color: theme.colors.primary
            }}>
              <FiDollarSign size={18} />
              <span>KES {price}</span>
            </div>
            
            {showPurchaseButton && !isOwned && (
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  if (onPurchaseClick) {
                    onPurchaseClick(course);
                  }
                }}
                style={{
                  padding: '6px 12px',
                  fontSize: '13px',
                  background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                }}
              >
                <FiShoppingCart size={14} /> Buy Now
              </Button>
            )}
            
            {isOwned && (
              <div style={{
                padding: '6px 12px',
                fontSize: '12px',
                fontWeight: '600',
                color: '#10B981',
                background: '#10B98115',
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}>
                <FiLock size={12} /> Owned
              </div>
            )}
          </div>
        )}
      </ContentCardBody>
    </ContentCard>
  );
};

export default CourseCard;
